import React, { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import "../css/SendEmails.css";
import "../css/CrmSearchBar.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { GoldLoader } from "../shared/Loaders";
import EmailPreviewEditor from "./EmailPreviewEditor";
import { normalizePersonId } from "../../utils/personId";
import SelectedRecipientsPanel from "./SelectedRecipientsPanel";
import SendResultModal from "./SendResultModal";
import { filterSendableTemplates } from "../../utils/sendableTemplates";

const SendEmails = () => {
    const prefersReducedMotion = useReducedMotion();
    const rowTransition = prefersReducedMotion
        ? { duration: 0 }
        : { type: "spring", stiffness: 500, damping: 40 };
    const API_URL = process.env.REACT_APP_API_URL;

    const [step, setStep] = useState("compose"); // "compose" | "preview"
    const [previewView, setPreviewView] = useState("carousel"); // "accordion" | "carousel"
    const [openAccordionId, setOpenAccordionId] = useState(null);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const [individualSelected, setIndividualSelected] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [sendResult, setSendResult] = useState(null); // { sent, failed: string[], error? }
    const [persons, setPersons] = useState([]);
    const [isLoadingPersons, setIsLoadingPersons] = useState(true);
    const [groupsMap, setGroupsMap] = useState({});
    const [searchIndividuals, setSearchIndividuals] = useState("");
    const [groupFilter, setGroupFilter] = useState(""); // selected group's _id, or "" for all groups
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [activeCompetitionId, setActiveCompetitionId] = useState(null);
    const [rawTemplateBody, setRawTemplateBody] = useState("");

    // Sort state for the recipient table — selected rows always stay pinned
    // at the top; only the unselected rows below reorder when a header is
    // clicked.
    const [sortState, setSortState] = useState({ col: null, dir: "asc" });

    const [orgEmail, setOrgEmail] = useState("");

    const [editorHtml, setEditorHtml] = useState("");
    const [organisations, setOrganisations] = useState({});
    const [personRoles, setPersonRoles] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [editablePreviews, setEditablePreviews] = useState({});
    const quillRef = useRef(null);
    const editorRef = useRef(null);

    const [fromName] = useState("IGF Team");
    const [fromEmail] = useState("tnaparna174@gmail.com");

    const [placeholders] = useState([
        "{{name}}",
        "{{email}}",
        "{{phone}}",
        // "{{competition}}",
    ]);

    const [formData, setFormData] = useState({
        name: "",
        subject: "",
        body: "",
        files: [],
        group_id: "",
        competition_id: "",
    });

    // Convert Organisation Name → Organisation Email (OPTION B)
    const orgNameToEmail = (name) => {
        if (!name) return "-";
        const clean = name.toLowerCase().replace(/[^a-z0-9]/g, "");
        return `info@${clean}.org`;
    };

    const getOrganisationName = (personId, orgMap, roles) => {
        if (!personId || !roles.length) return "-";
        const relations = roles.filter((role) => role.person_id === personId);
        const orgNames = relations.map((relation) => orgMap[relation.org_id]).filter(Boolean).join(", ");
        return orgNames || "N/A";
    };

    const determineOrganisationEmail = (recipient) => {
        if (!recipient || !recipient.organisation) return "";
        const email = orgNameToEmail(recipient.organisation);
        return email;
    };

    const safeParsePersonData = (rawData) => {
        if (!rawData) return {};

        let processed = rawData;

        while (typeof processed === "string") {
            try {
            const parsed = JSON.parse(processed);

            // Handle {"0":"{","1":"\""} corruption
            if (parsed && typeof parsed === "object" && parsed["0"] === "{") {
                processed = Object.values(parsed).join("");
            } else {
                processed = parsed;
            }
            } catch {
            return {};
            }
        }

        return typeof processed === "object" ? processed : {};
        };


    // Fetch templates
    const fetchCompetitions = async () => {
        try {
            const res = await axios.get(
            `${API_URL}/ems/competitions?active=true`
            );

            const data =
            res.data?.data ||
            res.data?.competitions ||
            res.data;

            setCompetitions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch competitions", err);
        }
        };
    useEffect(() => {
        fetchCompetitions();
    }, [API_URL]);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await axios.get(`${API_URL}/ems/templates`);
                setTemplates(filterSendableTemplates(res.data));
            } catch (err) {
                console.error("Failed to fetch templates", err);
            }
        };
        fetchTemplates();
    }, [API_URL]);

    // Keep preview updated for selected template


    // Fetch participant + org + roles
    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const [participantsRes, orgRes, personRolesRes] = await Promise.all([
                    axios.get(`${API_URL}/ems/participants`, { params: { slim: true } }),
                    axios.get(`${API_URL}/organisations`),
                    axios.get(`${API_URL}/personRoles`)
                ]);

                const data = participantsRes.data?.data ||
                    participantsRes.data?.participants ||
                    participantsRes.data ||
                    [];

                const personRolesData = personRolesRes.data;
                setPersonRoles(personRolesData);

                const orgMap = orgRes.data.reduce((map, org) => {
                    map[org._id] = org.name;
                    return map;
                }, {});
                setOrganisations(orgMap);

                const uniquePersonsMap = {};
                const newGroupsMap = {};

                data.forEach((p) => {
                    if (p.person_id) {
                        const personId = normalizePersonId(p.person_id._id);
                        if (!personId) return;
                        const meta = p.person_data ? safeParsePersonData(p.person_data) : {};

                        const firstName = meta.givenFirstName || p.person_id.name || "";
                        const lastName = meta.familyLastName || p.person_id.lname || "";
                        const fullName = ` ${firstName} ${lastName}`.trim();

                        let groupNames = [];
                        let groupIds = [];
                        if (p.group_id) {
                            const groupsToCheck = Array.isArray(p.group_id) ? p.group_id : [p.group_id];

                            groupsToCheck.forEach((group) => {
                                const gId = group._id;
                                if (!newGroupsMap[gId]) {
                                    newGroupsMap[gId] = {
                                        groupId: gId,
                                        group_name: group.group_name,
                                        persons: [],
                                    };
                                }

                                const orgEmailClean = orgNameToEmail(getOrganisationName(personId, orgMap, personRolesData));

                                // Prevent duplicate person entries in the group's person list
                                if (!newGroupsMap[gId].persons.some(member => member.personId === personId)) {
                                    newGroupsMap[gId].persons.push({
                                        personId: personId,
                                        name: `${fullName} - ${orgEmailClean}`,
                                    });
                                }
                                groupNames.push(group.group_name);
                                groupIds.push(gId);
                            });
                        }

                        if (!uniquePersonsMap[personId]) {
                            const orgName = getOrganisationName(personId, orgMap, personRolesData);
                            const orgEmailClean = orgNameToEmail(orgName);

                            uniquePersonsMap[personId] = {
                                _id: personId,
                                firstName: firstName,
                                lastName: lastName,
                                fullName: fullName,
                                email: p.person_id.email,
                                organisation: orgName,
                                organisationEmail: orgEmailClean,
                                group: groupNames.length > 0 ? groupNames.join(", ") : "-",
                                groupIds: groupIds, // Keep track of associated group Ids
                            };
                        }
                    }
                });

                setPersons(Object.values(uniquePersonsMap));
                setGroupsMap(newGroupsMap);
            } catch (err) {
                console.error("Failed to fetch participants", err);
            } finally {
                setIsLoadingPersons(false);
                setLoading(false);
            }
        };

        fetchParticipants();
    }, [API_URL]);


    useEffect(() => {
    const recipients = getRecipientsForPreview();
    const newPreviews = { ...editablePreviews };

    recipients.forEach(person => {
        const personId = normalizePersonId(person._id);
        newPreviews[personId] = replacePlaceholders(rawTemplateBody, person);
    });

    setEditablePreviews(newPreviews);
}, [rawTemplateBody, individualSelected]);

    // Unified filter: search box matches name/email only, group dropdown
    // narrows by membership — both combine over the same person list (no
    // more separate Individuals/Groups tabs).
    const filteredIndividuals = persons.filter((p) => {
        const s = (searchIndividuals || "").toLowerCase();
        const matchesSearch = !s ||
            (p.fullName || "").toLowerCase().includes(s) ||
            (p.email || "").toLowerCase().includes(s);
        const matchesGroup = !groupFilter || (p.groupIds || []).includes(groupFilter);
        return matchesSearch && matchesGroup;
    });

    // Selected rows pinned at the top (in their existing order); only the
    // unselected rows below reorder by the clicked column.
    const sortRows = (rows, selectedIds) => {
        const { col, dir } = sortState;
        const selected = rows.filter((p) => selectedIds.includes(p._id));
        const unselected = rows.filter((p) => !selectedIds.includes(p._id));
        if (col) {
            unselected.sort((a, b) => {
                const av = (a[col] || "").toString().toLowerCase();
                const bv = (b[col] || "").toString().toLowerCase();
                if (av < bv) return dir === "asc" ? -1 : 1;
                if (av > bv) return dir === "asc" ? 1 : -1;
                return 0;
            });
        }
        return selected.concat(unselected);
    };

    const toggleSort = (col) => {
        setSortState((prev) =>
            prev.col === col
                ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
                : { col, dir: "asc" }
        );
    };

    const sortedIndividuals = sortRows(filteredIndividuals, individualSelected);

    const handleSelectAllIndividuals = () => {
        const allPersonIds = filteredIndividuals.map((p) => p._id);
        setIndividualSelected((prev) => Array.from(new Set([...prev, ...allPersonIds])));
    };

    const handleDeselectAllIndividuals = () => {
        const visiblePersonIds = filteredIndividuals.map((p) => p._id);
        setIndividualSelected((prev) => prev.filter((id) => !visiblePersonIds.includes(id)));
    };

    const handleIndividualCheckboxChange = (id) => {
        setIndividualSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleRemoveRecipient = (personId) => {
        setIndividualSelected((prev) => prev.filter((id) => id !== personId));
    };

    // --- Utility Functions ---

    const getRecipientsForPreview = () => {
            const uniqueIds = Array.from(new Set(individualSelected));
            return persons.filter(p => uniqueIds.includes(p._id));
            };

    // ✅ DERIVED PREVIEW DATA (AUTO-UPDATES)


    const replacePlaceholders = (templateBody, person) => {
    if (!templateBody) return "";

    return templateBody
        .replace(/{{name}}/g, person?.fullName || "")
        .replace(/{{email}}/g, person?.email || "")
        .replace(/{{phone}}/g, person?.phone || "")
        .replace(/{{competition}}/g, person?.competition || "");
};

    const recipientsForPreview = getRecipientsForPreview();

    const previewHtmlList = getRecipientsForPreview().map(person => {
    const personId = normalizePersonId(person._id);
    return {
        personId,
        name: person.fullName,
        html:
            editablePreviews[personId] ||
            replacePlaceholders(rawTemplateBody, person),
    };
});

    const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").trim();

    // Wraps {{placeholder}} tokens in <mark> so the raw template preview
    // reads as a real (if unresolved) email instead of a wall of text.
    const highlightPlaceholders = (html) =>
        (html || "").replace(/\{\{\s*[\w]+\s*\}\}/g, (match) => `<mark>${match}</mark>`);

    // Reset preview UI to a sane default each time Preview is entered.
    useEffect(() => {
        if (step === "preview") {
            setOpenAccordionId(previewHtmlList[0]?.personId ?? null);
            setCarouselIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // Close the template-preview modal on Escape.
    useEffect(() => {
        if (!showTemplatePreview) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setShowTemplatePreview(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showTemplatePreview]);

    const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    setShowTemplatePreview(false);

    const chosen = templates.find((t) => t._id === templateId);
    if (!chosen) {
        setEditorHtml("");
        setFormData(prev => ({ ...prev, subject: "" }));
        return;
    }

    const templateBody = chosen.body || chosen.templateBody || chosen.content || "";
    setRawTemplateBody(templateBody);   // store raw template
    setEditorHtml(templateBody);        // editor content
    setFormData(prev => ({ ...prev, subject: chosen.subject || "" })); // ✅ populate subject
};


    const insertPlaceholder = (ph) => {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();

        if (range) {
            editor.insertText(range.index, ph);
        } else {
            editor.insertText(editor.getLength(), ph);
        }
    };
    const insertPlaceholderIntoPreview = (personId, placeholder) => {
    const currentContent = editablePreviews[personId] || "";
    // This appends the placeholder to the end of the current content
    // If you want exact cursor placement, it requires a more complex ref-per-id setup
    const newContent = currentContent + placeholder;

    setEditablePreviews(prev => ({
        ...prev,
        [personId]: newContent
    }));
};
    const getAllRecipients = () => {
    const uniqueIds = Array.from(new Set(individualSelected));

    return persons.filter(p => uniqueIds.map(String).includes(String(p._id)));
};


    const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSending) return;

    const recipients = getAllRecipients();
    if (!recipients.length) return alert("Select recipients");
    if (!formData.subject) return alert("Subject required");

    const payload = recipients.map((p) => {
        const personId = normalizePersonId(p._id);
        const bodyContent = editablePreviews[personId] || editablePreviews[p._id] || rawTemplateBody;

        return {
            personId,
            subject: formData.subject,
            body: replacePlaceholders(bodyContent, p),
            from_name: fromName,
            email: fromEmail,
        };
    });

    setIsSending(true);
    try {
        const res = await axios.post(`${API_URL}/ems/send-emails`, {
            from_name: fromName,
            email: fromEmail,
            emails: payload,
        });

        // results[] lines up index-for-index with the payload we sent
        const results = Array.isArray(res.data?.results) ? res.data.results : [];
        const sentIds = [];
        const failed = [];
        recipients.forEach((p, i) => {
            if (results[i]?.status === "success") sentIds.push(String(p._id));
            else failed.push(p.name || p.email || "Unknown recipient");
        });

        setSendResult({ sent: sentIds.length, failed });
        if (sentIds.length) {
            setIndividualSelected((prev) => prev.filter((id) => !sentIds.includes(String(id))));
        }
        if (!failed.length) setStep("compose");
    } catch (err) {
        console.error(err);
        setSendResult({ sent: 0, failed: [], error: true });
    } finally {
        setIsSending(false);
    }
};


    // Total Recipient Count
    const selectedRecipientCount = getAllRecipients().length;

    if (loading) {
        return <GoldLoader label="Loading..." variant="page" />;
    }

    const sortArrow = (col) => {
        if (sortState.col !== col) return <span className="sort-arrow">⇅</span>;
        return <span className="sort-arrow sort-arrow-active">{sortState.dir === "asc" ? "▲" : "▼"}</span>;
    };

    return (
        <div className="send-email-container">
            <div className="step-track">
                <span className={`dot ${step === "compose" ? "current" : "done"}`} />
                <span>Step 1 &middot; Compose</span>
                <span className="sep" />
                <span className={`dot ${step === "preview" ? "current" : ""}`} />
                <span>Step 2 &middot; Preview</span>
            </div>

            <p className="eyebrow">Communication &middot; Compose</p>
            <h4>Send Email</h4>
            <p className="subtitle">
                {step === "compose"
                    ? "Choose a template, pick recipients, then move to Preview to personalize each email."
                    : "Personalize each recipient's email, then send."}
            </p>

            {step === "compose" && (
                <>
                    <div className="card mb-3">
                        <div className="recip-head">
                            <label className="field-label" htmlFor="tpl-select" style={{ margin: 0 }}>
                                Choose Template
                            </label>
                            <button
                                type="button"
                                className="btn"
                                style={{ padding: "4px 12px", fontSize: "12px" }}
                                onClick={() => {
                                    if (!selectedTemplate) {
                                        alert("Choose a template first.");
                                        return;
                                    }
                                    setShowTemplatePreview((prev) => !prev);
                                }}
                            >
                                Preview Template
                            </button>
                        </div>
                        <select
                            id="tpl-select"
                            className="form-select tpl-select"
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                        >
                            <option value="">-- Select a template --</option>
                            {templates.map((t) => (
                                <option key={t._id} value={t._id}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {showTemplatePreview && (
                        <div
                            className="modal-overlay"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) setShowTemplatePreview(false);
                            }}
                        >
                            <div className="modal-box modal-box-wide">
                                <div className="modal-head">
                                    <div>
                                        <span className="modal-title">
                                            {templates.find((t) => t._id === selectedTemplate)?.name || "Template Preview"}
                                        </span>
                                        <p className="modal-subtitle">
                                            Raw template &mdash; placeholders shown unresolved, as authored
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="modal-close"
                                        aria-label="Close"
                                        onClick={() => setShowTemplatePreview(false)}
                                    >
                                        &times;
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {rawTemplateBody ? (
                                        <div
                                            className="editor-mock"
                                            dangerouslySetInnerHTML={{
                                                __html: highlightPlaceholders(rawTemplateBody),
                                            }}
                                        />
                                    ) : (
                                        <div className="editor-mock editor-mock--empty">
                                            (no content on this template)
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <SelectedRecipientsPanel
                        recipients={getAllRecipients()}
                        onRemove={handleRemoveRecipient}
                    />

                    <div className="card mb-3">
                        <div className="recipient-toolbar mb-3">
                            <div className="crm-search-wrapper">
                                <FontAwesomeIcon icon={faMagnifyingGlass} className="crm-search-icon" />
                                <input
                                    type="text"
                                    className="crm-search-input form-control"
                                    placeholder="Search individuals by name or email"
                                    value={searchIndividuals}
                                    onChange={(e) => setSearchIndividuals(e.target.value)}
                                />
                            </div>
                            <select
                                className="form-select tpl-select"
                                style={{ flex: 1, minWidth: "160px" }}
                                value={groupFilter}
                                onChange={(e) => setGroupFilter(e.target.value)}
                            >
                                <option value="">All groups</option>
                                {Object.values(groupsMap).map((g) => (
                                    <option key={g.groupId} value={g.groupId}>
                                        {g.group_name} ({g.persons.length})
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn"
                                onClick={handleSelectAllIndividuals}
                                disabled={
                                    filteredIndividuals.length === 0 ||
                                    filteredIndividuals.every(p => individualSelected.includes(p._id))
                                }
                            >
                                Select All
                            </button>
                            <button
                                type="button"
                                className="btn"
                                onClick={handleDeselectAllIndividuals}
                                disabled={
                                    filteredIndividuals.length === 0 ||
                                    filteredIndividuals.every(p => !individualSelected.includes(p._id))
                                }
                            >
                                Deselect All
                            </button>
                        </div>

                        <div className="ledger-wrap">
                            <div className="ledger-scroll">
                                <table className="table recipient-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "5%" }}>Select</th>
                                            <th
                                                className="sortable"
                                                style={{ width: "30%" }}
                                                onClick={() => toggleSort("fullName")}
                                            >
                                                Name{sortArrow("fullName")}
                                            </th>
                                            <th
                                                className="sortable"
                                                style={{ width: "35%" }}
                                                onClick={() => toggleSort("email")}
                                            >
                                                Email{sortArrow("email")}
                                            </th>
                                            <th
                                                className="sortable"
                                                style={{ width: "30%" }}
                                                onClick={() => toggleSort("group")}
                                            >
                                                Group(s){sortArrow("group")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoadingPersons ? (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : sortedIndividuals.length > 0 ? (
                                            sortedIndividuals.map((p) => {
                                                const isSelected = individualSelected.includes(p._id);
                                                return (
                                                    <motion.tr
                                                        key={p._id}
                                                        layout="position"
                                                        transition={rowTransition}
                                                        className={isSelected ? "row-selected" : ""}
                                                    >
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() =>
                                                                    handleIndividualCheckboxChange(p._id)
                                                                }
                                                            />
                                                        </td>
                                                        <td className="name-cell">{p.fullName}</td>
                                                        <td>{p.email}</td>
                                                        <td className="group-pill">{p.group}</td>
                                                    </motion.tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted">
                                                    No one matches this search/group.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => {
                                setIndividualSelected([]);
                                setSearchIndividuals("");
                                setGroupFilter("");
                                setSelectedTemplate("");
                                setEditorHtml("");
                                setFormData(prev => ({ ...prev, subject: "" }));
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-send"
                            disabled={!selectedTemplate || selectedRecipientCount === 0}
                            onClick={() => setStep("preview")}
                        >
                            Next (Preview)
                        </button>
                    </div>
                </>
            )}

            {step === "preview" && (
                <form onSubmit={handleSubmit}>
                    <div className="card">
                        <div className="preview-head">
                            <span className="section-label">
                                Personalize &amp; Preview &middot; {previewHtmlList.length} recipients
                            </span>
                            <div className="view-toggle">
                                <button
                                    type="button"
                                    className={previewView === "accordion" ? "active" : ""}
                                    onClick={() => setPreviewView("accordion")}
                                >
                                    Accordion
                                </button>
                                <button
                                    type="button"
                                    className={previewView === "carousel" ? "active" : ""}
                                    onClick={() => setPreviewView("carousel")}
                                >
                                    Carousel
                                </button>
                            </div>
                        </div>

                        {previewView === "accordion" ? (
                            <div>
                                {previewHtmlList.map(p => (
                                    <div
                                        key={p.personId}
                                        className={`acc-item ${openAccordionId === p.personId ? "open" : ""}`}
                                    >
                                        <div
                                            className="acc-header"
                                            onClick={() =>
                                                setOpenAccordionId(
                                                    openAccordionId === p.personId ? null : p.personId
                                                )
                                            }
                                        >
                                            <span className="acc-name">{p.name}</span>
                                            <span className="acc-snippet">{stripHtml(p.html)}</span>
                                            <span className="acc-chevron" />
                                        </div>
                                        {openAccordionId === p.personId && (
                                            <div className="acc-body">
                                                <div className="ph-row">
                                                    {placeholders.map(ph => (
                                                        <button
                                                            key={ph}
                                                            type="button"
                                                            className="ph-chip"
                                                            onClick={() =>
                                                                insertPlaceholderIntoPreview(p.personId, ph)
                                                            }
                                                        >
                                                            {ph}
                                                        </button>
                                                    ))}
                                                </div>
                                                <EmailPreviewEditor
                                                    value={editablePreviews[p.personId]}
                                                    onChange={(content) => {
                                                        setEditablePreviews(prev => ({
                                                            ...prev,
                                                            [p.personId]: content
                                                        }));
                                                    }}
                                                    placeholder="Customize this email..."
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            previewHtmlList.length > 0 && (
                                <div className="carousel-card">
                                    <div className="carousel-card-head">
                                        <span className="acc-name">
                                            {previewHtmlList[carouselIndex]?.name}
                                        </span>
                                        <span className="carousel-position">
                                            {carouselIndex + 1} of {previewHtmlList.length}
                                        </span>
                                    </div>
                                    <div className="carousel-card-body">
                                        <div className="ph-row">
                                            {placeholders.map(ph => (
                                                <button
                                                    key={ph}
                                                    type="button"
                                                    className="ph-chip"
                                                    onClick={() =>
                                                        insertPlaceholderIntoPreview(
                                                            previewHtmlList[carouselIndex].personId,
                                                            ph
                                                        )
                                                    }
                                                >
                                                    {ph}
                                                </button>
                                            ))}
                                        </div>
                                        <EmailPreviewEditor
                                            value={editablePreviews[previewHtmlList[carouselIndex]?.personId]}
                                            onChange={(content) => {
                                                setEditablePreviews(prev => ({
                                                    ...prev,
                                                    [previewHtmlList[carouselIndex].personId]: content
                                                }));
                                            }}
                                            placeholder="Customize this email..."
                                        />
                                    </div>
                                    <div className="carousel-nav">
                                        <button
                                            type="button"
                                            className="carousel-arrow"
                                            onClick={() =>
                                                setCarouselIndex(
                                                    (carouselIndex - 1 + previewHtmlList.length) %
                                                        previewHtmlList.length
                                                )
                                            }
                                        >
                                            &lsaquo;
                                        </button>
                                        <div className="carousel-dots">
                                            {previewHtmlList.map((p, i) => (
                                                <button
                                                    key={p.personId}
                                                    type="button"
                                                    className={`carousel-dot ${i === carouselIndex ? "active" : ""}`}
                                                    onClick={() => setCarouselIndex(i)}
                                                />
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            className="carousel-arrow"
                                            onClick={() =>
                                                setCarouselIndex((carouselIndex + 1) % previewHtmlList.length)
                                            }
                                        >
                                            &rsaquo;
                                        </button>
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    <div className="actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            disabled={isSending}
                            onClick={() => setStep("compose")}
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            className="btn-send"
                            disabled={isSending || selectedRecipientCount === 0 || !formData.subject || !editorHtml}
                        >
                            {isSending ? (
                                <>
                                    <Spinner animation="border" size="sm" role="presentation" className="btn-send__spinner" />
                                    Sending...
                                </>
                            ) : (
                                "Send Email"
                            )}
                        </button>
                    </div>
                </form>
            )}

            <SendResultModal result={sendResult} onClose={() => setSendResult(null)} />
        </div>
    );
};

export default SendEmails;
